import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

interface UiOption {
  id: string;
  label: string;
  count?: number;
}

@Component({
  selector: 'app-ui-tabs-segments',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ui-tabs-segments"
      [class]="containerClass()"
      [attr.role]="mode() === 'tabs' ? 'tablist' : 'group'"
      [attr.aria-label]="mode() === 'tabs' ? (ariaLabel() || 'Secciones') : null"
      tabindex="-1"
      (keydown)="onKeydown($event)"
    >
      @for (option of options(); track option.id) {
        <button
          type="button"
          [class]="buttonClass(option.id)"
          [attr.role]="mode() === 'tabs' ? 'tab' : null"
          [attr.id]="mode() === 'tabs' && idBase() ? idBase() + '-' + option.id : null"
          [attr.aria-controls]="mode() === 'tabs' && panelId() ? panelId() : null"
          [attr.aria-selected]="mode() === 'tabs' ? option.id === selectedId() : null"
          [attr.aria-pressed]="mode() === 'segments' ? option.id === selectedId() : null"
          [attr.tabindex]="mode() === 'tabs' ? tabIndexFor(option.id) : null"
          (click)="changed.emit(option.id)"
        >
          <span>{{ option.label }}</span>
          @if (option.count !== undefined) {
            <span class="ui-tabs-segments__count">{{ option.count }}</span>
          }
        </button>
      }
    </div>
  `,
  styles: [`
    .ui-tabs-segments { display: flex; flex-wrap: wrap; max-width: 100%; }
    .ui-tabs-segments--tabs { gap: calc(var(--esp-interno) / 3); border-bottom: 1px solid var(--border); margin-bottom: var(--esp-interno); overflow-x: auto; }
    .ui-tabs-segments--segments { gap: calc(var(--esp-interno) / 7); padding: calc(var(--esp-interno) / 5); border: 1px solid var(--border); border-radius: var(--radius-pill); background: var(--bg); overflow-x: auto; }
    .ui-tabs-segments button { border: none; background: transparent; color: var(--muted); font: inherit; font-weight: 500; cursor: pointer; white-space: nowrap; transition: color 120ms ease, border-color 120ms ease, background 120ms ease; }
    .ui-tabs-segments--tabs button { padding: calc(var(--esp-interno) - 4px) var(--esp-interno); margin-bottom: -1px; border-bottom: 2px solid transparent; font-size: var(--t-cuerpo); display: inline-flex; align-items: center; gap: calc(var(--esp-interno) / 2); }
    .ui-tabs-segments--segments button { padding: calc(var(--esp-interno) / 1.75) calc(var(--esp-interno) + 2px); border-radius: var(--radius-pill); font-size: var(--t-cuerpo-chico); }
    .ui-tabs-segments--tabs button:hover:not(.ui-tabs-segments__item--active),
    .ui-tabs-segments--segments button:hover:not(.ui-tabs-segments__item--active) { color: var(--primary); }
    .ui-tabs-segments__item--active { color: var(--primary); }
    .ui-tabs-segments--tabs .ui-tabs-segments__item--active { border-bottom-color: var(--primary); }
    .ui-tabs-segments--segments .ui-tabs-segments__item--active { background: var(--primary); color: var(--surface); box-shadow: var(--shadow-sm); }
    .ui-tabs-segments__count { margin-left: calc(var(--esp-interno) / 3); font-size: var(--t-micro); font-weight: 500; border-radius: var(--radius-pill); padding: calc(var(--esp-interno) / 6) calc(var(--esp-interno) / 2); background: var(--bg); color: var(--muted); }
    .ui-tabs-segments--tabs .ui-tabs-segments__item--active .ui-tabs-segments__count { background: var(--info-subtle); color: var(--info-tx); }
    @media (max-width: 768px) {
      .ui-tabs-segments--segments { border-radius: var(--radius); }
    }
  `],
})
export class UiTabsSegmentsComponent {
  readonly options = input.required<UiOption[]>();
  readonly selectedId = input.required<string>();
  readonly mode = input<'tabs' | 'segments'>('tabs');
  readonly ariaLabel = input('');
  readonly idBase = input('');
  readonly panelId = input('');
  readonly changed = output<string>();

  protected containerClass(): string {
    return `ui-tabs-segments ui-tabs-segments--${this.mode()}`;
  }

  protected buttonClass(id: string): string {
    return id === this.selectedId()
      ? 'ui-tabs-segments__item ui-tabs-segments__item--active'
      : 'ui-tabs-segments__item';
  }

  protected tabIndexFor(id: string): 0 | -1 {
    const options = this.options();
    const haySeleccionValida = options.some((option) => option.id === this.selectedId());
    if (haySeleccionValida) {
      return id === this.selectedId() ? 0 : -1;
    }

    return id === options[0]?.id ? 0 : -1;
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (this.mode() !== 'tabs') {
      return;
    }

    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') {
      return;
    }

    const tabs = Array.from((event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('[role="tab"]'));
    const activeIndex = tabs.indexOf(document.activeElement as HTMLElement);
    if (activeIndex === -1) {
      return;
    }

    event.preventDefault();

    if (event.key === 'Home') {
      tabs[0]?.focus();
      return;
    }

    if (event.key === 'End') {
      tabs[tabs.length - 1]?.focus();
      return;
    }

    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (activeIndex + delta + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
  }
}
